import React, { useState } from "react";
import "./style.css";

interface ApiPhase {
  duration: number;
  startDate: string;
  endDate: string;
}

interface AuditTimelineCardProps {
  auditCreatedDate: string;
  startDate: string;
  endDate: string;
  currentStage: string;
  completionPercent: number;
  actualStartDate: string | null;
  actualEndDate: string | null;
  scheduled: ApiPhase | null;
  inProgress: ApiPhase | null;
  closure: ApiPhase | null;
}

interface PhaseBar {
  key: "scheduled" | "inProgress" | "closure";
  label: string;
  startDay: number; // now decimal, relative to timelineStart
  endDay: number; // now decimal, relative to timelineStart
  color: "scheduled" | "in-progress" | "closure";
  details: {
    durationText: string;
    line1Label: string;
    line1Value: string;
    line2Label?: string;
    line2Value?: string;
    delayText?: string;
  };
}

interface MonthSection {
  monthName: string;
  year: number;
  daysInMonth: number;
  startDay: number; // 1-based index in global timeline
  endDay: number; // 1-based index in global timeline
  monthStartDay: number; // calendar day-of-month
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Keep: simple date-only formatter
const formatDate = (date: string | null) =>
  !date ? "-" : new Date(date).toLocaleDateString("en-GB");

// Keep: date-time formatter
const formatDateTime = (date: string | null) => {
  if (!date) return "-";
  const d = new Date(date);
  return `${d.toLocaleDateString("en-GB")}, ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

// Keep: original full-day diff (not used in bar calc now but not removed)
const getDaysDiff = (start: string, end: string) => {
  const s = new Date(start);
  const e = new Date(end);
  const diff = Math.ceil((e.getTime() - s.getTime()) / MS_PER_DAY);
  return diff <= 0 ? 1 : diff;
};

// NEW: normalize to midnight – used for timeline bounds / month sections
const normalizeDate = (d: string | Date) => {
  const dt = new Date(d);
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

// NEW: fractional day difference from base (includes hours/minutes)
const getDayDecimal = (dateStr: string, base: Date) => {
  const date = new Date(dateStr);
  return (date.getTime() - base.getTime()) / MS_PER_DAY; // 0.5 = half day
};

// NEW: duration formatter (A+B+D → days + hours + minutes)
const formatDuration = (start: string, end: string) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return "0h";
  const totalMinutes = Math.round(ms / (1000 * 60));

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  // < 1 hour → only minutes
  if (days === 0 && hours === 0) {
    return `${minutes}m`;
  }

  // < 1 day → hours + optional minutes
  if (days === 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`;
  }

  // ≥ 1 day → days + optional hours + optional minutes
  let str = `${days}d`;
  if (hours > 0) str += ` ${hours}h`;
  if (minutes > 0) str += ` ${minutes}m`;
  return str;
};

const getPhaseGradient = (color: string): string => {
  switch (color) {
    case "scheduled":
      return "linear-gradient(to right, #ef4444, #fb923c)";
    case "in-progress":
      return "linear-gradient(to right, #fde047, #bef264)";
    case "closure":
      return "linear-gradient(to right, #22c55e 0%, #22c55e 70%, #dc2626 70%, #dc2626 100%)";
    default:
      return "#94a3b8";
  }
};

const calculateMonthSections = (
  startDate: string,
  endDate: string
): MonthSection[] => {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);

  const sections: MonthSection[] = [];
  let current = new Date(start);
  let dayCounter = 1;

  while (current <= end) {
    const year = current.getFullYear();
    const month = current.getMonth();

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthName = current.toLocaleString("default", { month: "long" });

    const firstDay = current > new Date(year, month, 1) ? current.getDate() : 1;
    const lastDay =
      new Date(year, month, daysInMonth) > end ? end.getDate() : daysInMonth;

    const daysInRange = lastDay - firstDay + 1;

    sections.push({
      monthName,
      year,
      daysInMonth: daysInRange,
      startDay: dayCounter,
      endDay: dayCounter + daysInRange - 1,
      monthStartDay: firstDay,
    });

    dayCounter += daysInRange;
    current = new Date(year, month + 1, 1);
  }

  return sections;
};

const AuditTimelineCard: React.FC<AuditTimelineCardProps> = ({
  auditCreatedDate,
  startDate,
  endDate,
  actualStartDate,
  actualEndDate,
  scheduled,
  inProgress,
  closure,
  currentStage,
  completionPercent,
}) => {
  const [hoveredPhase, setHoveredPhase] = useState<PhaseBar | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showStatusTooltip, setShowStatusTooltip] = useState(false);

  // --- TIMELINE RANGE (based on all phase dates, fallback to planned) ---
  const phaseDateStrings = [
    scheduled?.startDate,
    scheduled?.endDate,
    inProgress?.startDate,
    inProgress?.endDate,
    closure?.startDate,
    closure?.endDate,
  ].filter((d): d is string => !!d && !isNaN(new Date(d).getTime()));

  let rawStart: Date;
  let rawEnd: Date;

  if (phaseDateStrings.length > 0) {
    rawStart = new Date(
      Math.min(...phaseDateStrings.map((d) => new Date(d).getTime()))
    );
    rawEnd = new Date(
      Math.max(...phaseDateStrings.map((d) => new Date(d).getTime()))
    );
  } else {
    rawStart = new Date(startDate);
    rawEnd = new Date(endDate);
  }

  const timelineStart = normalizeDate(rawStart); // base: 00:00 that day
  // Extend timeline end to include the full last day (23:59:59)
  const timelineEndDate = normalizeDate(rawEnd);
  const timelineEnd = new Date(timelineEndDate.getTime() + MS_PER_DAY - 1);
  const totalDays = Math.ceil(
    (timelineEnd.getTime() - timelineStart.getTime()) / MS_PER_DAY
  );

  const MIN_PIXELS_PER_DAY = 30; // Adjusted to fit without scroll
  const calculatedMinWidth = totalDays * MIN_PIXELS_PER_DAY;

  // prevent invalid ISO crash
  const startISO = isNaN(timelineStart.getTime())
    ? new Date().toISOString() // fallback safe
    : timelineStart.toISOString();

  const endISO = isNaN(timelineEndDate.getTime())
    ? new Date().toISOString()
    : timelineEndDate.toISOString();

  // safe month generation
  const monthSections = calculateMonthSections(startISO, endISO);

  const phases = [
    scheduled && { key: "scheduled", title: "Scheduled", ...scheduled },
    inProgress && { key: "inProgress", title: "In Progress", ...inProgress },
    closure && { key: "closure", title: "Closure", ...closure },
  ].filter(Boolean) as any[];

  const phaseBars: PhaseBar[] = [];

  phases.forEach((phase, index) => {
    let startDecimal = getDayDecimal(phase.startDate, timelineStart);
    let endDecimal = getDayDecimal(phase.endDate, timelineStart);

    startDecimal = Math.max(0, Math.min(startDecimal, totalDays));
    endDecimal = Math.max(startDecimal, Math.min(endDecimal, totalDays));

    // Determine if we should show end date based on next phase
    let line1Label: string;
    let line1Value: string;
    let line2Label: string | undefined;
    let line2Value: string | undefined;

    if (phase.key === "scheduled" && inProgress) {
      // Scheduled: show both start and end date if In Progress exists
      line1Label = "Started:";
      line1Value = formatDateTime(phase.startDate);
      line2Label = "Ended:";
      line2Value = formatDateTime(phase.endDate);
    } else if (phase.key === "inProgress" && closure) {
      // In Progress: show both start and end date if Closure exists
      line1Label = "Started:";
      line1Value = formatDateTime(phase.startDate);
      line2Label = "Ended:";
      line2Value = formatDateTime(phase.endDate);
    } else if (phase.key === "closure") {
      // Closure: always show closed on (end date)
      line1Label = "Closed On:";
      line1Value = formatDateTime(phase.endDate);
    } else {
      // Default: show start date only
      line1Label = "Started:";
      line1Value = formatDateTime(phase.startDate);
    }

    phaseBars.push({
      key: phase.key,
      label: phase.title,
      startDay: startDecimal,
      endDay: endDecimal,
      color:
        phase.key === "scheduled"
          ? "scheduled"
          : phase.key === "inProgress"
            ? "in-progress"
            : "closure",
      details: {
        durationText: `Duration: ${formatDuration(
          phase.startDate,
          phase.endDate
        )}`,
        line1Label,
        line1Value,
        line2Label,
        line2Value,
      },
    });
  });

  const rows = [
    { key: "scheduled", label: "Scheduled" },
    { key: "inProgress", label: "In Progress" },
    { key: "closure", label: "Closure" },
  ];
  const calcPos = (d: number) => {
    const safe = Math.max(0, Math.min(d, totalDays));
    const result = (safe / totalDays) * 100 - 1;
    return result;
  };

  const calcWidth = (s: number, e: number) => {
    const span = e - s;
    const percent = (span / totalDays) * 100;
    const result = Math.max(percent, 0);

    return result;
  };

  return (
    <div className="timeline-container">
      {/* --- Existing header info (kept) --- */}
      <div className="auditHeader">
        Audit Created: <strong>{formatDate(auditCreatedDate)}</strong>
      </div>

      <div className="dateInfoGrid">
        <div>
          <div className="dateLabel">Planned Start Date</div>
          <div className="dateValue">{formatDate(startDate)}</div>
        </div>
        <div>
          <div className="dateLabel">Planned End Date</div>
          <div className="dateValue">{formatDate(endDate)}</div>
        </div>
        <div>
          <div className="dateLabel">Actual Start Date</div>
          <div className="dateValue">{formatDate(actualStartDate)}</div>
        </div>
        <div>
          <div className="dateLabel">Actual End Date</div>
          <div className="actualEnd">{formatDate(actualEndDate)}</div>
        </div>
      </div>

      <div className="progressSection">
        <div className="progressHeader">
          <div className="label">Current Status</div>
        </div>

        <div className="progressWrapper">
          <div className="progressBarContainer">
            <div
              className="progressFill"
              style={{
                width: `${completionPercent}%`,
                background:
                  "linear-gradient(to right, #f97316, #fbbf24, #22c55e)",
              }}
              onMouseEnter={() => setShowStatusTooltip(true)}
              onMouseLeave={() => setShowStatusTooltip(false)}
            />

            {showStatusTooltip && (
              <div
                className="statusTooltip"
                style={{
                  left: `${completionPercent}%`,
                  transform: "translateX(-50%)",
                }}
              >
                {currentStage}
                <div className="tooltipArrow" />
              </div>
            )}
          </div>

          <div className="progressPercent">{completionPercent}%</div>
        </div>
      </div>

      <div className="timelineTitle">Audit Phase Timeline</div>
      <div className="timelineSubtitle">
        Visualizing each audit phase duration and response period
      </div>

      <div className="timelineChart">
        <div className="scrollWrapper">
          <div className="timelineGrid">
            <div className="phaseLabels">
              <div className="phaseHeader">Phase</div>
              <div className="phaseLabel">Scheduled</div>
              <div className="phaseLabel">In Progress</div>
              <div className="phaseLabel">Closure</div>
            </div>

            <div className="timelineContent">
              {/* --- Month header (kept, but day labels aligned from timelineStart) --- */}
              <div className="timelineHeader">
                <div className="monthSections">
                  {monthSections.map((section, idx) => {
                    const daysInSection = section.endDay - section.startDay + 1;
                    const widthPercent = (daysInSection / totalDays) * 100;

                    const pixelsPerDay =
                      ((widthPercent / 100) * calculatedMinWidth) /
                      daysInSection;
                    let step = 1;

                    // Dynamically adjust step based on pixel width per day
                    if (pixelsPerDay < 15)
                      step = 7; // Very tight: show every 7th day
                    else if (pixelsPerDay < 20)
                      step = 5; // Tight: show every 5th day
                    else if (pixelsPerDay < 25)
                      step = 3; // Moderate: show every 3rd day
                    else if (pixelsPerDay < 35) step = 2; // Comfortable: show every 2nd day
                    // else step = 1 (show all days)

                    return (
                      <div
                        key={idx}
                        className="monthSection"
                        style={{
                          width: `${widthPercent}%`,
                        }}
                      >
                        <div className="monthLabel">
                          {section.monthName} {section.year} ({daysInSection}{" "}
                          Days)
                        </div>

                        <div className="dayHeaders">
                          {Array.from({ length: daysInSection }, (_, i) => {
                            const globalIndex = section.startDay - 1 + i; // 0-based
                            const d = new Date(timelineStart);
                            d.setDate(d.getDate() + globalIndex);
                            const dayValue = d.getDate();

                            // Show first day, last day, or every step-th day
                            const show =
                              i === 0 ||
                              i === daysInSection - 1 ||
                              i % step === 0;

                            return (
                              <div
                                key={i}
                                className="dayHeader"
                                style={{
                                  visibility: "visible" ,
                                }}
                              >
                                {dayValue}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* --- Bar area with day grid lines ONLY here --- */}
              <div className="ganttBarArea">
                {/* vertical lines between each day */}
                <div className="ganttDayGrid">
                  {Array.from({ length: Math.round(totalDays) }).map((_, i) => (
                    <div key={i} className="ganttDayColumn" />
                  ))}
                </div>

                {rows.map((row) => {
                  const bar = phaseBars.find((b) => b.key === row.key);
                  return (
                    <div key={row.key} className="phaseRow">
                      <div className="phaseTimelineBarContainer">
                        {bar ? (
                          <div
                            className="phaseTimelineBar"
                            style={{
                              left: `${calcPos(bar.startDay)}%`,
                              width: `${calcWidth(bar.startDay, bar.endDay)}%`,
                              background: getPhaseGradient(bar.color),
                              cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                              setHoveredPhase(bar);
                              const rect = (
                                e.target as HTMLElement
                              ).getBoundingClientRect();
                              setTooltipPos({
                                x: rect.left + rect.width / 2,
                                y: rect.top - 10,
                              });
                            }}
                            onMouseLeave={() => setHoveredPhase(null)}
                          />
                        ) : (
                          <div></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {hoveredPhase && (
        <div
          className="timelineTooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y - (hoveredPhase.details.line2Label ? 130 : 110),
            transform: "translateX(-50%)",
          }}
        >
          <div className="tooltip-content">
            <div className="tooltipTitle">{hoveredPhase.label}</div>
            <div className="tooltipRow">
              {hoveredPhase.details.durationText}
            </div>
            <div className="tooltipRow">
              {hoveredPhase.details.line1Label}{" "}
              {hoveredPhase.details.line1Value}
            </div>
            {hoveredPhase.details.line2Label &&
              hoveredPhase.details.line2Value && (
                <div className="tooltipRow">
                  {hoveredPhase.details.line2Label}{" "}
                  {hoveredPhase.details.line2Value}
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTimelineCard;
